import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  Flex, 
  Grid, 
  GridItem, 
  Button, 
  Card, 
  CardBody, 
  CardFooter,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Image,
  Badge,
  Link,
  useColorModeValue
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useWeb3 } from '../lib/Web3Provider';
import ConnectWallet from '../components/ConnectWallet';
import { formatTokenId, formatNumber } from '../lib/utils';

interface Token {
  id: string;
  uri: string;
  totalSupply: string;
  name: string;
  image?: string;
}

const HomePage: React.FC = () => {
  const { connected, readContract } = useWeb3();
  const [loading, setLoading] = useState(false);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [totalTypes, setTotalTypes] = useState<number>(0);

  const fetchTokens = async () => {
    if (!readContract) return;
    
    setLoading(true);
    try {
      // Get total token types
      const typeSupply = await readContract.typeSupply();
      const count = typeSupply.toNumber();
      setTotalTypes(count);
      
      // Fetch the first few tokens as examples
      const tokenPromises = [];
      const max = Math.min(count, 12); // Limit to 12 tokens for display
      
      for (let i = 1; i <= max; i++) {
        tokenPromises.push(fetchTokenDetails(i));
      }
      
      const fetchedTokens = await Promise.all(tokenPromises);
      setTokens(fetchedTokens.filter(t => t !== null) as Token[]);
    } catch (error) {
      console.error('Error fetching tokens:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTokenDetails = async (index: number): Promise<Token | null> => {
    try {
      // Get token ID from index
      const id = await readContract.tokenByIndex(index);
      const tokenId = id.toString();
      
      // Get token URI
      let uri = '';
      try {
        uri = await readContract.uri(tokenId);
      } catch (e) {
        console.warn(`Could not get URI for token ${tokenId}`);
      }
      
      // Get token supply
      const supply = await readContract.totalSupply(tokenId);
      
      // Default name
      let name = `Token #${formatTokenId(tokenId)}`;
      let image = undefined;
      
      // Try to fetch metadata if URI is available
      if (uri && uri.startsWith('ipfs://')) {
        try {
          const ipfsUrl = uri.replace('ipfs://', 'https://freeweb3.infura-ipfs.io/ipfs/');
          const response = await fetch(ipfsUrl);
          if (response.ok) {
            const metadata = await response.json();
            if (metadata.name) name = metadata.name;
            if (metadata.image) image = metadata.image;
          }
        } catch (e) {
          console.warn(`Could not fetch metadata for ${tokenId}`);
        }
      }
      
      return {
        id: tokenId,
        uri,
        totalSupply: supply.toString(),
        name,
        image
      };
    } catch (error) {
      console.error(`Error fetching token at index ${index}:`, error);
      return null;
    }
  };
  
  useEffect(() => {
    if (connected && readContract) {
      fetchTokens();
    }
  }, [connected, readContract]);
  
  if (!connected) {
    return <ConnectWallet />;
  }
  
  return (
    <Container maxW="container.xl" py={8}>
      <Box textAlign="center" mb={10}>
        <Heading as="h1" size="2xl" mb={3}>
          SmartLaw Token Manager
        </Heading>
        <Text fontSize="xl" color="gray.500">
          Create, mint, and configure SmartLaw tokens on Polygon
        </Text>
      </Box>
      
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} mb={14}>
        <Stat bg={useColorModeValue("blue.50", "blue.900")} p={5} borderRadius="lg" boxShadow="sm">
          <StatLabel fontSize="lg">Total Token Types</StatLabel>
          <StatNumber fontSize="3xl">{formatNumber(totalTypes)}</StatNumber>
          <StatHelpText>Unique token IDs</StatHelpText>
        </Stat>
        
        <Stat bg={useColorModeValue("green.50", "green.900")} p={5} borderRadius="lg" boxShadow="sm">
          <StatLabel fontSize="lg">Contract</StatLabel>
          <StatNumber fontSize="md">0x3059B722...92A1</StatNumber>
          <StatHelpText>
            <Link href="https://polygonscan.com/address/0x3059B722579949459a7A778771Ec5e2e990392A1" isExternal color={useColorModeValue("blue.500", "blue.300")}>
              View on PolygonScan
            </Link>
          </StatHelpText>
        </Stat>
        
        <Stat bg={useColorModeValue("purple.50", "purple.900")} p={5} borderRadius="lg" boxShadow="sm">
          <StatLabel fontSize="lg">Network</StatLabel>
          <StatNumber fontSize="2xl">Polygon</StatNumber>
          <StatHelpText>Mainnet</StatHelpText>
        </Stat>
      </SimpleGrid>
      
      <Box mb={10}>
        <Flex justify="space-between" align="center" mb={5}>
          <Heading as="h2" size="lg">
            Recent Tokens
          </Heading>
          <Button as={RouterLink} to="/explore" colorScheme="blue" variant="outline">
            View All
          </Button>
        </Flex>
        
        {loading ? (
          <Flex justify="center" align="center" h="300px">
            <Spinner size="xl" thickness="4px" color="blue.500" />
          </Flex>
        ) : (
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }} gap={6}>
            {tokens.map(token => (
              <GridItem key={token.id}>
                <Card h="100%" bg={useColorModeValue("white", "gray.800")}>
                  <CardBody>
                    <Box position="relative" mb={4}>
                      {token.image ? (
                        <Image 
                          src={token.image.startsWith('ipfs://') 
                            ? token.image.replace('ipfs://', 'https://freeweb3.infura-ipfs.io/ipfs/') 
                            : token.image
                          }
                          alt={token.name}
                          borderRadius="md"
                          height="150px"
                          width="100%"
                          objectFit="cover"
                          fallbackSrc="https://via.placeholder.com/150?text=Token"
                        />
                      ) : (
                        <Box 
                          height="150px" 
                          width="100%" 
                          bg={useColorModeValue("gray.100", "gray.700")} 
                          borderRadius="md"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="lg" color={useColorModeValue("gray.500", "gray.400")}>No Image</Text>
                        </Box>
                      )}
                      
                      <Badge 
                        position="absolute" 
                        top={2} 
                        right={2} 
                        colorScheme="blue"
                      >
                        Supply: {token.totalSupply}
                      </Badge>
                    </Box>
                    
                    <Heading size="md" mb={2} noOfLines={1}>
                      {token.name}
                    </Heading>
                    
                    <Text color={useColorModeValue("gray.500", "gray.400")} fontSize="sm" noOfLines={1}>
                      ID: {formatTokenId(token.id)}
                    </Text>
                  </CardBody>
                  
                  <CardFooter bg={useColorModeValue("white", "gray.800")} pt={0}>
                    <Button 
                      as={RouterLink}
                      to={`/configure?tokenId=${token.id}`}
                      variant="solid" 
                      colorScheme="blue" 
                      size="sm"
                      width="full"
                    >
                      Configure
                    </Button>
                  </CardFooter>
                </Card>
              </GridItem>
            ))}
          </Grid>
        )}
      </Box>
      
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} mb={10}>
        <GridItem>
          <Card h="100%" bg={useColorModeValue("white", "gray.800")}>
            <CardBody>
              <Heading size="md" mb={3}>
                Create New Token
              </Heading>
              <Text mb={4}>
                Create new token types with role-based permissions
              </Text>
            </CardBody>
            <CardFooter pt={0} bg={useColorModeValue("white", "gray.800")}>
              <Button as={RouterLink} to="/create" colorScheme="blue" width="full">
                Create Token
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
        
        <GridItem>
          <Card h="100%" bg={useColorModeValue("white", "gray.800")}>
            <CardBody>
              <Heading size="md" mb={3}>
                Mint & Distribute
              </Heading>
              <Text mb={4}>
                Mint tokens to individual addresses or many at once
              </Text>
            </CardBody>
            <CardFooter pt={0} bg={useColorModeValue("white", "gray.800")}>
              <Button as={RouterLink} to="/mint" colorScheme="green" width="full">
                Mint Tokens
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
        
        <GridItem>
          <Card h="100%" bg={useColorModeValue("white", "gray.800")}>
            <CardBody>
              <Heading size="md" mb={3}>
                Configure Tokens
              </Heading>
              <Text mb={4}>
                Set metadata URIs and manage supply limits
              </Text>
            </CardBody>
            <CardFooter pt={0} bg={useColorModeValue("white", "gray.800")}>
              <Button as={RouterLink} to="/configure" colorScheme="purple" width="full">
                Configure
              </Button>
            </CardFooter>
          </Card>
        </GridItem>
      </Grid>
    </Container>
  );
};

export default HomePage;